import React, { useState } from "react";
import {
  Container, Card, Typography, TextField, Button, Box, CircularProgress,
  Alert, Link
} from "@mui/material";
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { useNavigate } from "react-router-dom";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Step 1: Request code
  const [email, setEmail] = useState("");

  // Step 2: Confirm reset
  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await resetPassword({ username: email });
      setSuccess("Verification code sent to your email.");
      setStep('confirm');
    } catch (err: any) {
      setError(err.message || "Failed to send reset code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    // Validate password strength
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      setLoading(false);
      return;
    }

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: newPassword,
      });
      setSuccess("Password reset successfully! Redirecting to sign in...");
      setTimeout(() => navigate("/signin"), 2000);
    } catch (err: any) {
      setError(err.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xs" sx={{ mt: 6 }}>
      <Card sx={{ p: 4, boxShadow: 3, borderRadius: 3 }}>
        <Typography variant="h5" align="center" fontWeight="bold" gutterBottom>
          {step === 'request' ? 'Forgot Password' : 'Reset Password'}
        </Typography>

        {step === 'request' && (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Enter your email address and we'll send you a verification code to reset your password.
          </Typography>
        )}

        {step === 'confirm' && (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Enter the verification code sent to <strong>{email}</strong> and your new password.
          </Typography>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Step 1: Request reset code */}
        {step === 'request' && (
          <Box component="form" onSubmit={handleRequestCode}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{ mb: 3 }}
            />

            <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading} sx={{ py: 1.5 }}>
              {loading ? <CircularProgress size={24} color="inherit" /> : "Send Reset Code"}
            </Button>

            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Link component="button" variant="body2" onClick={() => navigate("/signin")}>
                Back to Sign In
              </Link>
            </Box>
          </Box>
        )}

        {/* Step 2: Enter code + new password */}
        {step === 'confirm' && (
          <Box component="form" onSubmit={handleConfirmReset}>
            <TextField
              fullWidth
              label="Verification Code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              sx={{ mb: 2 }}
              placeholder="Enter the 6-digit code"
            />
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              sx={{ mb: 2 }}
              helperText="Minimum 8 characters with uppercase, lowercase, number, and symbol"
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              sx={{ mb: 3 }}
            />

            <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading} sx={{ py: 1.5 }}>
              {loading ? <CircularProgress size={24} color="inherit" /> : "Reset Password"}
            </Button>

            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
              <Link component="button" variant="body2" onClick={() => { setStep('request'); setError(null); setSuccess(null); }}>
                Resend Code
              </Link>
              <Link component="button" variant="body2" onClick={() => navigate("/signin")}>
                Back to Sign In
              </Link>
            </Box>
          </Box>
        )}
      </Card>
    </Container>
  );
};

export default ForgotPassword;
